/**
 * eSign Service
 * Handles electronic signature capture and storage
 */

import {
  createStorageMetadata,
  recordDocumentMetadata,
  type DocumentUploadContext,
} from '@/firebase/documentRegistry';

export interface Signature {
  dataUrl: string; // Base64 encoded signature image
  signerName?: string;
  signerEmail?: string;
  signedAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

export interface SignatureMetadata {
  signatureUrl?: string; // Firebase Storage URL if uploaded
  signatureDataUrl?: string; // Base64 data URL (alternative storage)
  signerName?: string;
  signerEmail?: string;
  signedAt: Date;
  signatureType: 'draw' | 'typed' | 'upload';
}

export interface SignatureUploadOptions extends Partial<DocumentUploadContext> {}

/**
 * Convert signature canvas to base64 data URL
 */
export function canvasToDataUrl(canvas: HTMLCanvasElement, format: 'image/png' | 'image/jpeg' = 'image/png'): string {
  return canvas.toDataURL(format);
}

/**
 * Upload signature image to Firebase Storage
 */
export async function uploadSignature(
  signatureDataUrl: string,
  fileName?: string,
  options: SignatureUploadOptions = {}
): Promise<string> {
  // Import Firebase Storage dynamically to avoid circular dependencies
  const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
  const { storage } = await import('@/firebase/config');

  if (!storage) {
    throw new Error('Firebase Storage not initialized');
  }

  try {
    // Convert data URL to blob
    const response = await fetch(signatureDataUrl);
    const blob = await response.blob();

    // Import shared filename sanitization utility
    const { sanitizeFileName } = await import('@/utils/fileNameSanitizer');

    // Create a unique filename
    const timestamp = Date.now();
    const rawSignatureFileName = fileName || `signature_${timestamp}.png`;
    const signatureFileName = sanitizeFileName(rawSignatureFileName);
    const filePath = `signatures/${timestamp}_${signatureFileName}`;
    
    console.log('[ESignService] Filename sanitization', {
      original: rawSignatureFileName,
      sanitized: signatureFileName,
      finalPath: filePath,
    });
    const uploadedAt = new Date().toISOString();

    const storageContext: DocumentUploadContext = {
      docType: options.docType || 'signature',
      organizationId: options.organizationId,
      locationId: options.locationId,
      taskId: options.taskId,
      checklistId: options.checklistId,
      formId: options.formId,
      agreementId: options.agreementId,
      assetId: options.assetId,
      uploadedBy: options.uploadedBy,
      templateId: options.templateId,
      title: options.title || signatureFileName,
      source: options.source || 'web',
      additionalMetadata: options.additionalMetadata,
    };

    // Upload to Firebase Storage
    const storageRef = ref(storage, filePath);
    await uploadBytes(
      storageRef,
      blob,
      createStorageMetadata(storageContext, {
        contentType: blob.type,
        uploadedAt,
      })
    );

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    await recordDocumentMetadata({
      storagePath: storageRef.fullPath,
      bucket: (storageRef as any).bucket,
      downloadUrl: downloadURL,
      mimeType: blob.type,
      sizeBytes: blob.size,
      uploadedAt,
      context: storageContext,
    });

    return downloadURL;
  } catch (error: any) {
    console.error('Error uploading signature:', error);
    throw new Error(`Signature upload failed: ${error.message}`);
  }
}

/**
 * Create signature metadata object
 */
export function createSignatureMetadata(
  dataUrl: string,
  signerName?: string,
  signerEmail?: string,
  signatureType: 'draw' | 'typed' | 'upload' = 'draw'
): SignatureMetadata {
  return {
    signatureDataUrl: dataUrl,
    signerName: signerName,
    signerEmail: signerEmail,
    signedAt: new Date(),
    signatureType,
  };
}

